// Soraka Wish VFX — soft healing effect with no attack-like motion.
// Phase 1: Gentle breathing glow at Soraka; glowing motes drift inward.
// Phase 2: Motes float one-by-one along a lazy arc toward the target (bezier, no beam).
// Phase 3: Soft ripple rings bloom at the target like a gentle pulse.

import type { Animation } from "../../engine/animation/AnimationQueue";
import { Easing } from "../../engine/animation/Easing";
import type { Point } from "../../engine/hex/HexLayout";

const BREATHE_MS = 650;
const FLOAT_MS   = 900;
const BLOOM_MS   = 350;
const TOTAL_MS   = BREATHE_MS + FLOAT_MS + BLOOM_MS;
const MOTE_COUNT = 7;
const MOTE_WINDOW = 0.65; // fraction of FLOAT phase each mote's journey occupies

export class SorakaWishVFX implements Animation {
  private elapsed = 0;
  private readonly moteAngles: number[];

  constructor(
    private from: Point,
    private to: Point,
    private hexSize: number,
  ) {
    this.moteAngles = Array.from(
      { length: MOTE_COUNT },
      (_, i) => (i / MOTE_COUNT) * Math.PI * 2,
    );
  }

  update(dt: number): void {
    this.elapsed = Math.min(this.elapsed + dt, TOTAL_MS);
  }

  draw(ctx: CanvasRenderingContext2D): void {
    const t = this.elapsed;
    if (t < BREATHE_MS) {
      this.drawBreathe(ctx, t / BREATHE_MS);
    } else if (t < BREATHE_MS + FLOAT_MS) {
      this.drawFloat(ctx, (t - BREATHE_MS) / FLOAT_MS);
    } else {
      this.drawBloom(ctx, (t - BREATHE_MS - FLOAT_MS) / BLOOM_MS);
    }
  }

  // Pulsing soft orb + motes drifting slowly inward.
  private drawBreathe(ctx: CanvasRenderingContext2D, p: number): void {
    const { from, hexSize } = this;
    const pulse = 0.5 + 0.5 * Math.sin(p * Math.PI * 3.5);

    // Soft breathing glow — no hard ring edge
    ctx.save();
    ctx.globalAlpha = 0.15 + 0.2 * pulse;
    ctx.fillStyle = "#fff8d0";
    ctx.shadowColor = "#ffeea0";
    ctx.shadowBlur = 28;
    ctx.beginPath();
    ctx.arc(from.x, from.y, hexSize * (0.45 + 0.15 * pulse), 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();

    // Motes gently converging inward from outside
    for (let i = 0; i < MOTE_COUNT; i++) {
      const angle = this.moteAngles[i];
      const r = hexSize * 0.95 * (1 - p * 0.55);
      // Subtle lateral wobble, gentle upward float
      const wobble = Math.sin(p * Math.PI * 2.5 + i * 1.1) * hexSize * 0.06;
      const x = from.x + Math.cos(angle) * r + wobble;
      const y = from.y + Math.sin(angle) * r - p * hexSize * 0.18;
      const fadeIn = Math.min(1, p * 4);
      this.drawMote(ctx, x, y, 2 + p * 1.5, fadeIn * 0.55);
    }
  }

  // Motes float one-by-one along a soft bezier arc — no beam, no straight line.
  private drawFloat(ctx: CanvasRenderingContext2D, p: number): void {
    const { from, to, hexSize } = this;

    // Control point arcs upward above the midpoint so the path curves like a loft.
    const cx = (from.x + to.x) / 2;
    const cy = (from.y + to.y) / 2 - hexSize * 2.2;

    for (let i = 0; i < MOTE_COUNT; i++) {
      // Stagger launch: earlier motes start first
      const startP = (i / MOTE_COUNT) * (1 - MOTE_WINDOW);
      const endP   = startP + MOTE_WINDOW;
      if (p < startP || p > endP + 0.08) continue;

      const localP = Math.min((p - startP) / MOTE_WINDOW, 1);
      const eased  = Easing.easeInOutQuad(localP);

      // Quadratic bezier position
      const u  = 1 - eased;
      const bx = u * u * from.x + 2 * u * eased * cx + eased * eased * to.x;
      const by = u * u * from.y + 2 * u * eased * cy + eased * eased * to.y;

      // Fade in at launch, fade out on arrival
      const alpha = Math.sin(Math.PI * localP) * 0.75;
      const size  = 3 + 1.5 * Math.sin(Math.PI * localP);
      this.drawMote(ctx, bx, by, size, alpha);
    }
  }

  // Two soft ripple rings expand at the target — like a gentle heartbeat.
  private drawBloom(ctx: CanvasRenderingContext2D, p: number): void {
    const { to, hexSize } = this;

    for (let ring = 0; ring < 2; ring++) {
      const delay = ring * 0.35;
      if (p < delay) continue;
      const rp    = Math.min((p - delay) / (1 - delay), 1);
      const r     = hexSize * 0.65 * Easing.easeOutCubic(rp);
      const alpha = (1 - rp) * 0.45;

      ctx.save();
      ctx.globalAlpha = alpha;
      ctx.strokeStyle = "#b8ffd5";
      ctx.shadowColor  = "#80ffc0";
      ctx.shadowBlur   = 14;
      ctx.lineWidth    = 2.5 - rp;
      ctx.beginPath();
      ctx.arc(to.x, to.y, r, 0, Math.PI * 2);
      ctx.stroke();
      ctx.restore();
    }
  }

  private drawMote(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    size: number,
    alpha: number,
  ): void {
    ctx.save();
    ctx.globalAlpha = alpha;
    ctx.fillStyle   = "#fff8d0";
    ctx.shadowColor = "#ffeea0";
    ctx.shadowBlur  = 12;
    ctx.beginPath();
    ctx.arc(x, y, size, 0, Math.PI * 2);
    ctx.fill();
    // Tiny white heart at center
    ctx.globalAlpha = alpha * 0.55;
    ctx.fillStyle   = "#ffffff";
    ctx.shadowBlur  = 0;
    ctx.beginPath();
    ctx.arc(x, y, size * 0.38, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }

  isDone(): boolean {
    return this.elapsed >= TOTAL_MS;
  }
}
